import React, { useEffect, useState } from 'react';
import { GridRowsProp, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Box, Chip, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import _ from 'lodash';
import HAppRoutes from '../../../../../app/features/routes/helpers/HAppRoutes';
import InvestorRoutes from '../../../../investor/InvestorRoutes';
import { useAuthContext } from '../../../../auth/contexts/AuthContext';
import { useAppNavigationContext } from '../../../../../app/features/navigation/providers/AppNavigationProvider';
import CAppDataGrid from '../../../../../app/components/CAppDataGrid';
import AppCustomIcon from '../../../../../app/features/icon/AppIcon';
import { CheckOutlined, CloseOutlined } from '@mui/icons-material';
import moment from 'moment';
import IUserProfile from '../../../interfaces/IUserProfile';
import { useTranslation } from 'react-i18next';
import langAdminInvestors from '../../../langs/langAdminInvestors';

type CTableInvestorsProps = {
	investors: IUserProfile[];
	investorsForRedirect?: IUserProfile[];
	hideColumns?: string[];
};
const CTableInvestors = (props: CTableInvestorsProps) => {
	const { investors, investorsForRedirect, hideColumns } = props;

	const navigate = useNavigate();

	const { t } = useTranslation();

	const { setAuthOverrideUserId, redirectUri, resetRedirectUri, setCurrentCompany } = useAuthContext();
	const appNavigationContext = useAppNavigationContext();

	const handleLogin = (id: number) => {
		setAuthOverrideUserId(id);
		setCurrentCompany(null);
		appNavigationContext.setDisplayShareLink(true);
		if (redirectUri) {
			const redirectUriTmp = _.clone(redirectUri);
			resetRedirectUri();
			navigate(redirectUriTmp, { replace: true });
		} else {
			navigate(HAppRoutes.getPath(InvestorRoutes.dashboard.tag), { replace: true });
		}
	};

	const [rows, setRows] = useState<GridRowsProp | null>(null);

	useEffect(() => {
		if (investors) {
			setRows(
				_.orderBy(
					investors.map((x) => {
						return {
							...x,
							forRedirect: investorsForRedirect && investorsForRedirect.map((r) => r.id).includes(x.id),
							companyName: x.companies.map((c) => (
								<Chip size="small" key={`${c.id}-${c.group}`} label={c.name} />
							)),
						};
					}),
					['companyName'],
					['asc'],
				),
			);
		}
	}, [investors, investorsForRedirect]);

	const columns: GridColDef[] = [
		{
			field: 'id',
			headerName: t(langAdminInvestors.table_header_open.tag),
			width: 70,
			align: 'center',
			headerAlign: 'center',
			cellClassName: 'select-investor',
			renderCell: (params: GridRenderCellParams) => (
				<IconButton
					disabled={!params.row.isAccountCreated}
					id={`investor-${params.row.id}`}
					color="primary"
					onClick={() => handleLogin(params.value)}
				>
					<AppCustomIcon tag="search" />
				</IconButton>
			),
		},
		{
			field: 'companyName',
			headerName: t(langAdminInvestors.table_header_groups.tag),
			minWidth: 200,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => (
				<Box
					id={`company-name-for-investor-${params.row.id}`}
					sx={{
						overflow: 'auto',
						'&::-webkit-scrollbar': {
							display: 'none',
						},
						'&:hover': {
							'&::-webkit-scrollbar': {
								display: 'block',
							},
							'&::-webkit-scrollbar-thumb': {
								backgroundColor: '#888',
								borderRadius: '20px',
							},
							'&::-webkit-scrollbar-track': {
								backgroundColor: '#eee',
								borderRadius: '20px',
							},
						},
					}}
				>
					{params.value}
				</Box>
			),
		},
		{
			field: 'firstName',
			headerName: t(langAdminInvestors.table_header_firstname.tag),
			width: 150,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'lastName',
			headerName: t(langAdminInvestors.table_header_lastname.tag),
			width: 150,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'login',
			headerName: t(langAdminInvestors.table_header_login.tag),
			width: 200,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'email',
			headerName: t(langAdminInvestors.table_header_email.tag),
			width: 200,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'defaultLanguage',
			headerName: t(langAdminInvestors.table_header_lang.tag),
			width: 70,
			align: 'center',
			headerAlign: 'center',
		},

		{
			field: 'isAccountCreated',
			headerName: t(langAdminInvestors.table_header_okta_sync.tag),
			disableColumnMenu: true,
			width: 100,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) =>
				params.value ? <CheckOutlined color="success" /> : <CloseOutlined color="error" />,
		},
		{
			field: 'isAccountActivated',
			headerName: t(langAdminInvestors.table_header_active.tag),
			disableColumnMenu: true,
			width: 70,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) =>
				params.value ? <CheckOutlined color="success" /> : <CloseOutlined color="error" />,
		},
		{
			field: 'importCreationTime',
			headerName: t(langAdminInvestors.table_header_created_at.tag),
			width: 100,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) =>
				params.value !== null ? <Box>{moment(params.value).format('DD/MM/YYYY')}</Box> : <>n.c.</>,
		},
		{
			field: 'importDeletionTime',
			headerName: t(langAdminInvestors.table_header_deleted_at.tag),
			width: 100,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) =>
				params.value !== null ? (
					<Box>{moment(params.value).format('DD/MM/YYYY')}</Box>
				) : (
					<>
						<CheckOutlined color="success" /> Actif
					</>
				),
		},
	];

	return (
		<>
			{rows ? (
				<Box>
					<CAppDataGrid
						className="admin-investors-table"
						getRowClassName={(params) => `row-status--${params.row.fundIsRealized}`}
						rows={rows}
						columns={hideColumns ? columns.filter((x) => !hideColumns.includes(x.field)) : columns}
						autoHeight
						pagination
					/>
				</Box>
			) : (
				''
			)}
		</>
	);
};

export default CTableInvestors;
